import { ChangeDetectionStrategy, Component, Inject, inject } from '@angular/core';
import { NetworkableDestroyableComponent } from '../networkable-destroyable.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { shareReplayComponentConfig } from '../../constants';
import { FormControl } from '@angular/forms';
import { SnackBarService } from '../../services/snackbar.service';
import { LoadService } from '../../../modules/internal/loads/load.service';

export interface DispatchSystemOption {
  value: string;
  displayName: string;
  hint?: string;
  disabled?: boolean;
}

export interface DispatchSystemStatus {
  loadId: string;
  currentSystem: string;
  availableSystems: DispatchSystemOption[];
  warnings: string[];
}

@Component({
  selector: 'td-dispatch-system-selector',
  templateUrl: './dispatch-system-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DispatchSystemSelectorComponent extends NetworkableDestroyableComponent {
  public loadId: string;
  private dispatchSystemStatus$$ = new BehaviorSubject<DispatchSystemStatus>(null);
  public dispatchSystemStatus$ = this.dispatchSystemStatus$$
    .asObservable()
    .pipe(shareReplay(shareReplayComponentConfig));
  private loadService = inject(LoadService);
  private snackbar = inject(SnackBarService);
  private matDialogRef: MatDialogRef<DispatchSystemSelectorComponent> = inject(MatDialogRef);
  public selectedSystemFC = new FormControl<string>('');

  // Available dispatch systems
  private dispatchSystems: DispatchSystemOption[] = [
    {
      value: 'other',
      displayName: 'Other',
      hint: 'Nothing is set in the DB. This load will continue to exist on whichever dispatch system it is on.',
      disabled: true,
    },
    {
      value: 'vpf-autodispatch',
      displayName: 'VPF Autodispatch',
      hint: 'This is the VPF autodispatch pool. Needs to have a valid pool ID for it to work.',
    },
    {
      value: 'drayage',
      displayName: 'Drayage',
      disabled: true,
    },
    {
      value: 'flex-loadboard',
      displayName: 'Flex Loadboard',
      hint: 'Setting this sends the load to the Flex Loadboard.',
    },
    {
      value: 'otr',
      displayName: 'OTR',
      hint: 'This is any OTR dispatch not of drayage.',
      disabled: true,
    },
  ];

  constructor(@Inject(MAT_DIALOG_DATA) data: { loadId: string }) {
    super();
    this.loadId = data.loadId;
    this.getDispatchSystem(this.loadId);
  }

  private async getDispatchSystem(loadId: string) {
    this.networkActive$$.next(true);
    try {
      const status = await this.loadService.getDispatchSystem(loadId);

      // Create a default response if the API doesn't return expected format
      const systemStatus: DispatchSystemStatus = {
        loadId,
        currentSystem: status?.dispatchSystem || 'other',
        availableSystems: this.dispatchSystems,
        warnings: status?.warnings || [],
      };

      this.dispatchSystemStatus$$.next(systemStatus);
      this.selectedSystemFC.setValue(systemStatus.currentSystem);
    } finally {
      this.networkActive$$.next(false);
    }
  }

  public getCurrentSystemName(status: DispatchSystemStatus): string {
    return status.availableSystems.find((s) => s.value === status.currentSystem)?.displayName || 'Default';
  }

  public async updateDispatchSystem() {
    if (this.isActive) {
      return;
    }

    const newSystem = this.selectedSystemFC.value;
    if (!newSystem) {
      return;
    }

    try {
      this.isActive = true;
      const success = await this.loadService.updateDispatchSystem(this.loadId, newSystem);
      if (success) {
        this.snackbar.showMessage(`Dispatch system updated to ${this.getSystemDisplayName(newSystem)}`);
        this.matDialogRef.close();
      }
    } finally {
      this.isActive = false;
    }
  }

  public getSystemDisplayName(value: string): string {
    return this.dispatchSystems.find((s) => s.value === value)?.displayName || value;
  }

  public getSelectedSystemHint(): string | undefined {
    const selectedValue = this.selectedSystemFC.value;
    if (!selectedValue) {
      return undefined;
    }
    return this.dispatchSystems.find((s) => s.value === selectedValue)?.hint;
  }

  public trackByValue(index: number, item: DispatchSystemOption): string {
    return item.value;
  }

  public trackByWarning(index: number, warning: string): number {
    return index;
  }
}
